import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { compose } from 'redux';
import { CTA, CustomSelect, Modal, Text } from '../..';
import { apiHost, baseUrl } from '../../../../app/api/config';
import { withFileUploader } from '../../../../app/hoc/withFileUploader';
import { withMediaQueries } from '../../../../app/hoc/withMediaQueries';
import { MODAL_CLOSE } from '../../../../app/redux/actions';
import {
  GROUP_IMPORT_TEMPLATE_GET,
  USER_LIST_GET,
} from '../../../../app/redux/actions/tutors';
import {
  acceptedDocsTypes,
  getFileExtension,
  getIconByExtension,
} from '../../../../utils/fileExtension';
import {
  getValues,
  isValidForm,
  newForm,
  newFormField,
} from '../../../../utils/form';
import { formatFilterOpt } from '../../../../utils/labels';
import { groupsPath } from '../../../../utils/paths';
import { RegexpType, validator } from '../../../../utils/validator';
import { ToastError, ToastSuccess } from '../../../components/Toast';
import { CustomBox } from '../../../components/Tutor/style';
import CustomTooltip from '../../CustomTooltip';
import DropZone from '../../DropZone';
import { DropZoneWrapper } from '../../DropZone/style';
import InputLegend from '../../InputLegend';
import { Footer, ScrollableArea } from '../style';
import { ButtonsWrapper, FileImg, InputContainer } from './style';

export const ModalGroupImportId = 'ModalGroupImport';

const TYPE_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'Matricola', value: 'matricola' },
];

const ROLE_OPTIONS = [
  { label: 'Tutor', value: 'tutor' },
  { label: 'Cotutor', value: 'cotutor' },
  { label: 'Discente', value: 'consumer' },
];

const OPERATION_OPTIONS = [
  { label: 'Aggiungi utenti', value: 'add' },
  { label: 'Rimuovi utenti', value: 'remove' },
];

const initialForm = newForm([
  newFormField({
    field: 'type',
    required: true,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'role',
    required: true,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'ffoo',
    required: false,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'operation',
    required: true,
    type: RegexpType.STRING,
    valid: true,
  }),
]);

const ModalGroupImport = ({
  mediaIsPhone,
  isOpen,
  id,
  payload,
  onFileChange,
  modalClose,
  getUsers,
  downloadTemplate,
  filters,
}) => {
  const inputRef = useRef(null);
  const scrollableAreaRef = useRef(null);
  const [groupId, setGroupId] = useState();
  // form
  const [groupForm, setGroupForm] = useState(initialForm);
  const [file, setFile] = useState();
  const [validFormat, setValidFormat] = useState(true);

  useEffect(() => {
    if (payload?.id) setGroupId(payload?.id);
  }, [payload]);

  useEffect(() => {
    if (id === ModalGroupImportId) {
      //get filters
      getUsers({
        group_ids: -1,
      });
    }
    return () => {
      if (id === ModalGroupImportId) {
        setGroupForm(initialForm);
        setFile();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, id]);

  const handleOnChange = (field = '', newValue) => {
    const { type, required } = groupForm[field];

    const newGroupForm = {
      ...groupForm,
      [field]: {
        ...groupForm[field],
        value: newValue,
        valid:
          typeof newValue !== 'object'
            ? validator(type, newValue, required)
            : true,
      },
    };

    if (field === 'type') {
      newGroupForm.ffoo.required = newValue === 'matricola';
      newGroupForm.ffoo.value = '';
    }
    setGroupForm(newGroupForm);
  };

  // file uploader
  const uploadFile = (files = []) => {
    setFile(files[0]);
    if (inputRef) inputRef.current.value = null; //? clear input (otherwise onChange won't trigger)
  };

  const checkSupportedFiles = (files) => {
    let isSupported = true;
    files.forEach((f) => {
      if (acceptedDocsTypes.findIndex((t) => t === f.type) < 0) {
        isSupported = false;
      }
    });
    if (!isSupported) {
      setValidFormat(false);
    } else {
      uploadFile(files);
      setValidFormat(true);
    }
  };

  const handleFileDrop = (monitor) => {
    if (monitor) {
      const { files } = monitor.getItem();
      checkSupportedFiles(files);
    }
  };

  const handleImportError = (errorMsg) => {
    modalClose();
    const errorCode = errorMsg?.split(':')?.length
      ? errorMsg?.split(':')[0]
      : '';
    let msg = 'Si prega di riprova più tardi.';
    switch (errorCode) {
      case 'PG/IMPUGC01':
        msg =
          'File non valido, verifica il contenuto e di utilizzare il template corretto.';
        break;
      case 'PG/IMPUGC02':
        msg = 'Non è stato trovato nessun utente da importare.';
        break;
      case 'PG/IMPUGC03':
        msg = 'Non è stato possibile aggiungere il file in coda.';
        break;

      default:
        break;
    }

    toast.error(<ToastError title="Errore" body={msg} />);
  };

  const onConfirm = () => {
    onFileChange({
      file,
      key: 'file',
      body: getValues(groupForm),
      url: `${apiHost}${baseUrl}${groupsPath}group/${payload?.id}/users/import`,
      callback: () => {
        modalClose();
        toast.success(
          <ToastSuccess
            title="Operazione completata."
            body="Il file è stato aggiunto in coda e verrà processato. Riceverai una notifica con l'esito."
          />
        );
      },
      errorCallback: handleImportError,
    });
  };

  return (
    <Modal
      title="Import massivo gruppo"
      modalId={ModalGroupImportId}
      scrollableAreaRef={scrollableAreaRef}
    >
      <ScrollableArea ref={scrollableAreaRef} className="auto-height">
        <Text type="h2" mb={16}>
          {payload?.name}
        </Text>
        <ButtonsWrapper>
          <CTA
            type="secondary"
            icon="download-01"
            btnText="Scarica template email"
            onClick={() => downloadTemplate('email')}
          />
          <CTA
            type="secondary"
            icon="download-01"
            btnText="Scarica template matricola"
            onClick={() => downloadTemplate('matricola')}
          />
        </ButtonsWrapper>
        <CustomBox>
          {!groupId && <div className="overlay" />}
          <div className="d-flex mb-3">
            <Text type="h2">Aggiungi utenti al gruppo "{payload?.name}"</Text>
            <CustomTooltip
              type="secondary"
              title="Attenzione"
              tooltipKey="max_import_users"
            />
          </div>
          {file ? (
            <DropZoneWrapper className="d-flex">
              <FileImg
                src={getIconByExtension(getFileExtension(file?.name))}
                alt={
                  `${getFileExtension(file?.name)} file extension icon` ?? ''
                }
              />
              <Text type="b4"> {file?.name}</Text>
              <CTA
                type="tertiary"
                btnText="Elimina"
                icon="trash-01"
                onClick={() => setFile()}
              />
            </DropZoneWrapper>
          ) : (
            <DndProvider backend={HTML5Backend}>
              <DropZone
                onDrop={handleFileDrop}
                onClick={() => inputRef.current.click()}
                validFormat={validFormat}
                onlyFromPC
              />
              <input
                aria-label="Questo dispositivo"
                ref={inputRef}
                className="d-none"
                type="file"
                id="inputFile"
                onChange={(e) => uploadFile([e.target.files[0]])}
                accept={['text/csv']}
              />
            </DndProvider>
          )}
          <InputContainer>
            {/* type */}
            <div>
              <InputLegend label="Tipologia" required htmlFor="import-type" />
              <CustomSelect
                size={mediaIsPhone ? 'full' : 's'}
                options={TYPE_OPTIONS}
                selected={
                  TYPE_OPTIONS.find((f) => f.value === groupForm.type.value) ||
                  null
                }
                onChange={(opt) => handleOnChange('type', opt?.value)}
                isClearable
                id="import-type"
                menuPlacement="top"
              />
            </div>
            {/* role */}
            <div>
              <InputLegend label="Ruolo" required htmlFor="import-role" />
              <CustomSelect
                size={mediaIsPhone ? 'full' : 's'}
                options={ROLE_OPTIONS}
                selected={
                  ROLE_OPTIONS.find((f) => f.value === groupForm.role.value) ||
                  null
                }
                onChange={(opt) => handleOnChange('role', opt?.value)}
                isClearable
                id="import-role"
                menuPlacement="top"
              />
            </div>
            {/* operation */}
            <div>
              <InputLegend
                label="Operazione"
                required
                htmlFor="import-operation"
              />
              <CustomSelect
                size={mediaIsPhone ? 'full' : 's'}
                options={OPERATION_OPTIONS}
                selected={
                  OPERATION_OPTIONS.find(
                    (f) => f.value === groupForm.operation.value
                  ) || null
                }
                onChange={(opt) => handleOnChange('operation', opt?.value)}
                isClearable
                id="import-operation"
                menuPlacement="top"
              />
            </div>
            {/* ffoo */}
            {groupForm.type.value === 'matricola' && filters?.ffoo && (
              <div>
                <InputLegend
                  label="Forza dell'ordine"
                  required
                  htmlFor="import-ffoo"
                />
                <CustomSelect
                  size={mediaIsPhone ? 'full' : 's'}
                  options={filters.ffoo?.map((opt) => formatFilterOpt(opt))}
                  onChange={(opt) => handleOnChange('ffoo', opt?.value)}
                  isClearable
                  menuPlacement="top"
                />
              </div>
            )}
          </InputContainer>
        </CustomBox>
      </ScrollableArea>
      <Footer>
        <CTA
          btnText="Conferma"
          disabled={!isValidForm(groupForm) || !file}
          onClick={onConfirm}
        />
        <CTA type="secondary" btnText="Annulla" onClick={modalClose} />
      </Footer>
    </Modal>
  );
};

ModalGroupImport.propTypes = {
  //HOC: withMediaQueries
  mediaIsPhone: PropTypes.bool,
  // state
  isOpen: PropTypes.bool,
  id: PropTypes.string,
  payload: PropTypes.object,
  filters: PropTypes.object,

  // dispatch
  onFileChange: PropTypes.func.isRequired,
  modalClose: PropTypes.func.isRequired,
  getUsers: PropTypes.func.isRequired,
  downloadTemplate: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => {
      const { filters } = state.tutors.users;
      const { isOpen, id, payload } = state.app.modal;
      return { filters, isOpen, id, payload };
    },
    (dispatch) => ({
      modalClose: () => dispatch({ type: MODAL_CLOSE }),
      getUsers: (payload) =>
        dispatch({ type: USER_LIST_GET._REQUEST, payload }),
      downloadTemplate: (templateType) =>
        dispatch({ type: GROUP_IMPORT_TEMPLATE_GET._REQUEST, templateType }),
    })
  ),
  withFileUploader
)(withMediaQueries(ModalGroupImport));
